import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { baseHttpRequestOptions, userMessageErrorHandler } from '../services.utils';
import { environment } from '@environments/environment';
import { Observable, throwError } from 'rxjs';
import {
  Contract,
  ContractInstallment,
  ContractPaymentType,
  ContractInstallmentStatus,
  ContractBeneficiary,
  ContractType,
  ContractMembershipProposal,
} from '@app/core/models/contract/contract.model';
import { map, catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { defaultDialogConfig } from '@app/shared/configs/default.dialog.config';
import { FeedbackDialogComponent } from '@app/theme/feedback-dialog/feedback-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ContractsService {
  API_CONTRACTS_URL = environment.apiURL + '/' + 'contracts';
  sharedData: string;
  idCancel: string;
  sharedProtocol;

  constructor(private readonly httpClient: HttpClient, public dialog: MatDialog) {}

  getContracts(): Observable<Contract[]> {
    return this.httpClient
      .get<Contract[]>(this.API_CONTRACTS_URL, baseHttpRequestOptions)
      .pipe(
        map((response: any) => {
          const contracts: Contract[] = response.data;
          return contracts.map((contract) =>
            this.transformContractResponseToContractObject(contract)
          );
        })
      );
  }

  getCertificate(payload): Observable<any> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.httpClient
      .post<any>(this.API_CONTRACTS_URL + '/certificate', payload, options)
      .pipe(
        map((response: any) => {
          // MODAL OK?
          this.openFeedbackDialog('success', 'Sucesso! Certificado gerado com sucesso!');

          return response.data;
        }),
        catchError((response) => {
          if (this.dialog.openDialogs.length >= 1) {
            this.dialog.openDialogs[0].close();
          }

          this.openFeedbackDialog(
            'warning',
            'Ops! Certificado não localizado. Por favor, contate o SAC Sabemi: 0800 880 1900'
          );

          const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
          return throwError(message);
        })
      );
  }

  getMembershipProposal(contractID: number): Observable<ContractMembershipProposal> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    const urlRequest = `${this.API_CONTRACTS_URL}/${contractID}/GetMembershipProposal`;

    return this.httpClient.get<any>(urlRequest, options).pipe(
      map((response: any) => {
        // MODAL OK?
        this.openFeedbackDialog(
          'success',
          'Tudo OK! Proposta de adesão gerada com sucesso!'
        );

        return response.data;
      }),
      catchError((response) => {
        if (this.dialog.openDialogs.length >= 1) {
          this.dialog.openDialogs[0].close();
        }

        this.openFeedbackDialog(
          'warning',
          'Ops! Proposta de Adesão não localizada. Contate o SAC Sabemi: 0800 880 1900'
        );

        const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
        return throwError(message);
      })
    );
  }

  openFeedbackDialog(color, message, options?: { protocolNumber?: any }) {
    this.openFeedbackDialogConfig(FeedbackDialogComponent, {
      data: {
        color: color,
        message: message,
        protocolNumber: options?.protocolNumber,
      },
    });
  }

  openFeedbackDialogConfig(dialogComponent, data = {}) {
    return this.dialog.open(dialogComponent, {
      data,
      panelClass: 'feedback-modalbox',
      disableClose: true,
    });
  }

  openDialog(dialogComponent, data = {}) {
    return this.dialog.open(dialogComponent, {
      data,
      ...defaultDialogConfig,
      disableClose: true,
    });
  }

  updateContractPaymentType(
    contractID,
    data: {
      contractProductTypeID: number;
      paymentType: ContractPaymentType;
      fullName: string;
      registrationCode: string;
      organ: string;
      upag: string;
    }
  ) {
    return this.httpClient
      .put<any>(
        `${this.API_CONTRACTS_URL}/payment`,
        {
          idContract: contractID,
          fullName: data.fullName,
          idProductType: data.contractProductTypeID,
          paymentType: data.paymentType,
          registrationCode: data.registrationCode,
          organ: data.organ,
          upag: data.upag,
        },
        baseHttpRequestOptions
      )
      .pipe(
        map((response) => {
          this.sharedProtocol = response.data.protocol;
          return response;
        }),
        catchError(({ error: { errors } }) => {
          // MODAL OK?, MODAL API
          this.openFeedbackDialog('error', errors[0] ? errors[0] : 'Ocorreu um erro');

          return throwError(errors[0]);
        })
      );
  }

  getContractBenefits(contractID: number) {
    return this.httpClient.get<any>(
      `${this.API_CONTRACTS_URL}/${contractID}/benefits`,
      baseHttpRequestOptions
    );
  }

  getContractInstallments(
    contractID: number,
    contractProductTypeID
  ): Observable<ContractInstallment[]> {
    return this.httpClient
      .get<any>(
        `${this.API_CONTRACTS_URL}/${contractID}/installments?productTypeId=${contractProductTypeID}`,
        baseHttpRequestOptions
      )
      .pipe(
        map((response) => {
          let installments: ContractInstallment[] = [];

          response.data.installments.map((item) => {
            const now = new Date();
            const expirationDate = new Date(item.expirationDate);

            let installmentStatus = '';

            if (item.status) {
              installmentStatus = ContractInstallmentStatus.PAGO;
            } else {
              if (now > expirationDate) {
                installmentStatus =
                  contractProductTypeID === ContractType.FINANCIAL_ASSISTANCE
                    ? ContractInstallmentStatus.REGULARIZAR
                    : ContractInstallmentStatus.EM_ABERTO;
              } else {
                installmentStatus = ContractInstallmentStatus.A_VENCER;
              }
            }

            contractProductTypeID;

            let installment: ContractInstallment = {
              expirationDate: item.expirationDate,
              installmentNumber: item.installmentNumber,
              paymentDate: item.paymentDate,
              period: item.period,
              frequency: item.frequency,
              price: item.price,
              status: installmentStatus,
            };

            installments.push(installment);
          });

          return installments;
        })
      );
  }

  transformContractResponseToContractObject(contractResponse): Contract {
    return {
      ...contractResponse,
      payment: {
        type: contractResponse.paymentType,
      },
      validity: {
        startDate: contractResponse.startExpiryDate,
        endDate: contractResponse.endExpiryDate,
      },
    };
  }

  sendBeneficiaries(contractID, beneficiaries: ContractBeneficiary[]) {
    return this.httpClient
      .post<any>(
        `${this.API_CONTRACTS_URL}/${contractID}/beneficiary`,
        beneficiaries,
        baseHttpRequestOptions
      )
      .pipe(
        map((data: any) => {
          this.sharedProtocol = data.data.protocol;
          return data;
        }),
        catchError((response) => {
          const message =
            userMessageErrorHandler(response) || 'Erro ao cadastrar beneficiário!';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  getReasonsCancellation(): Observable<any> {
    return this.httpClient.get<any>(
      `${this.API_CONTRACTS_URL}/getreasonscancellation`,
      baseHttpRequestOptions
    );
  }

  cancellationContract(payload) {
    return this.httpClient
      .post<any>(
        `${this.API_CONTRACTS_URL}/cancellation`,
        payload,
        baseHttpRequestOptions
      )
      .pipe(
        map((data: any) => {
          // MODAL OK, MODAL API
          this.openFeedbackDialog('success', 'Sucesso! Contrato cancelado com sucesso!', {
            protocolNumber: data.data.protocol,
          });
          this.sharedProtocol = data.data.protocol;
          return data;
        }),
        catchError((response) => {
          const message =
            userMessageErrorHandler(response) || 'Erro ao cancelar contrato!';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  getPendingBallance(contractID): Observable<any> {
    return this.httpClient.get<any>(
      `${this.API_CONTRACTS_URL}/${contractID}/balance`,
      baseHttpRequestOptions
    );
  }

  getPendingNewBallance(payload): Observable<any> {
    return this.httpClient
      .post<any>(
        `${this.API_CONTRACTS_URL}/GetNewBalance`,
        payload,
        baseHttpRequestOptions
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((response) => {
          const message =
            userMessageErrorHandler(response) || 'Erro ao consultar Saldo Devedor!';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }
}
